import { flexRender } from "@tanstack/react-table"
import clsx from "clsx"
import { Fragment, forwardRef } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { TruncatableText } from "@particles/index"

import { Icon } from "@atoms"

import SortingIcons from "@organisms/Table/SortingIcons/SortingIcons"
import SkeletonCell from "@organisms/Table/TableCells/SkeletonCell/SkeletonCell"

import styles from "./TableHeaderCell.module.scss"
import { TableCellProps } from "./TableHeaderCell.types"

function TableHeaderCell(props: TableCellProps, ref: React.Ref<HTMLTableCellElement>) {
    const {
        leftStickyOffset,
        content,
        width,
        colSpan,
        alignment = "left",

        context,
        dataType,

        onClick,

        canSort,
        isSticky,
        isScrolling,
        isLastSticky,
        isSorted,
        isPlaceholder,
        isTabbable,
        isLoading,
        isLoadingAnimated,
    } = props

    const isDesktop = useIsDesktop()

    const onKeyDown = (event: React.KeyboardEvent<HTMLTableCellElement>) => {
        if (event.key === "Enter") {
            onClick?.(event)
        }
    }

    if (isLoading) {
        return <SkeletonCell isAnimated={isLoadingAnimated} variant="header" cellWidth={width} />
    } else {
        return (
            <th
                ref={ref}
                colSpan={colSpan}
                style={{
                    minWidth: width,
                    flexBasis: width,
                    left: isSticky ? `${leftStickyOffset}px` : "auto",
                    cursor: canSort ? "pointer" : "default",
                }}
                onClick={onClick}
                className={clsx(styles.base, styles[`${alignment}Align`], {
                    [styles.sticky]: isSticky,
                    [styles.sortable]: canSort,
                    [styles.scrolling]: isScrolling,
                    [styles.lastSticky]: isLastSticky,
                })}
                onKeyDown={onKeyDown}
                tabIndex={isTabbable ? 0 : -1}
            >
                {isSticky && isDesktop && (
                    <Icon icon={faIcon({ name: "thumbtack", style: "solid" })} size={10} container="fixedHeight" />
                )}
                <TruncatableText
                    tooltipOnTruncate={true}
                    tooltipPlacement="bottom"
                    text={isPlaceholder ? null : flexRender(content, context)}
                />
                {canSort && (
                    <span>
                        {isSorted && (
                            <Fragment>
                                {{
                                    asc: <SortingIcons isSorted={true} ascending={true} dataType={dataType} />,
                                    desc: <SortingIcons isSorted={true} ascending={false} dataType={dataType} />,
                                }[isSorted as string] ?? null}
                            </Fragment>
                        )}
                    </span>
                )}
            </th>
        )
    }
}

export default forwardRef(TableHeaderCell)
